import { useRef } from 'react'

import { groupBy, orderBy } from 'lodash'

import { useEffect } from 'modules/core/hooks/useEffect'

import { CostCenterAssignmentEditableCard } from 'modules/finance/components/CostCenterAssigments/Assigments/EditableCard'
import { NewAssignmentCard } from 'modules/finance/components/CostCenterAssigments/Assigments/NewAssignmentCard'
import { CostCenterAssignmentsTimeline } from 'modules/finance/components/CostCenterAssigments/Timeline'
import { CostCenterAssignment } from 'modules/finance/lib/costCenterAssignments'

interface Props {
  assignments: CostCenterAssignment[]
  children?: React.ReactNode
  legalEntityIds: number[]
  resourceId: number
  withAssignmentIdOpened?: number
  canManageCostCenterMemberships: boolean
  onCancel: () => void
  onDelete: () => void
  onSave: () => void
  onToggleEdit: (value: boolean) => void
}

export const Content = ({
  assignments,
  children,
  legalEntityIds,
  resourceId,
  withAssignmentIdOpened,
  canManageCostCenterMemberships,
  onCancel,
  onDelete,
  onSave,
  onToggleEdit,
}: Props) => {
  const groupedAssigments = Object.entries(
    orderBy(
      groupBy(
        assignments,
        (assignment) => `${assignment.startDate}-${assignment.endDate || 'null'}`
      ),
      (group) => group[0]?.startDate,
      'desc'
    )
  )

  const isFirstRender = useRef(true)

  // We want to check the first time the component is rendered. The reason is that
  // we're injecting new components in the timeline, and every time a new
  // component is injected, the component is rendered again.
  //
  // We're opening the current assignment when the sidepanel is opened by edit,
  // and this is preventing to reopened again when the component is re-rendered.
  useEffect(() => {
    isFirstRender.current = false
  }, [])

  if (groupedAssigments.length === 0 && canManageCostCenterMemberships) {
    return (
      <NewAssignmentCard
        allAssignments={assignments}
        legalEntityIds={legalEntityIds}
        resourceId={resourceId}
        onCancel={onCancel}
        onSave={onSave}
      />
    )
  }

  return (
    <CostCenterAssignmentsTimeline>
      {children}

      {groupedAssigments.map(([date, assigmentsByDate]) => {
        return (
          <CostCenterAssignmentEditableCard
            key={date}
            assignments={assigmentsByDate}
            allAssignments={assignments}
            legalEntityIds={legalEntityIds}
            canManageCostCenterMemberships={canManageCostCenterMemberships}
            onSave={onSave}
            onDelete={onDelete}
            resourceId={resourceId}
            onToggleEdit={onToggleEdit}
            openOnRender={
              isFirstRender.current && withAssignmentIdOpened === assigmentsByDate[0]?.id
            }
          />
        )
      })}
    </CostCenterAssignmentsTimeline>
  )
}
